import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { confirmarCodigo, reenviarCodigo, passoFunilVaga, criarDenuncia } from "../services/api";
import { payloadDoRelato, RASCUNHO_KEY } from "../components/ComplaintForm";
import { AvisoEmail, useCampoEmail } from "../components/AvisoEmail";

export default function LoginPage() {
  const { login, registrar } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // ?redirect= existe porque link de E-MAIL não carrega o state do router: quem clica em "ver candidatura"
  // e cai no login precisa voltar exatamente para o candidato, não para a home.
  const _redir = new URLSearchParams(location.search).get("redirect");
  const destino = location.state?.from || (_redir && _redir.startsWith("/") ? _redir : null) || "/";
  const motivo = location.state?.from ? location.state?.motivo : null; // "porquê" vindo do fluxo de publicar
  // Pré-seleção via query (?tipo=empresa&cadastro=1) — ex.: botão "Cadastrar empresa" cai direto no cadastro de EMPRESA.
  const _q = new URLSearchParams(location.search);
  const _cadastroQuery = _q.get("cadastro") === "1" || _q.get("modo") === "cadastro";
  const [modo, setModo] = useState((location.state?.from || _cadastroQuery) ? "cadastro" : "login");
  const [tipo, setTipo] = useState(_q.get("tipo") === "empresa" ? 1 : 0); // 0 candidato, 1 empresa
  const [form, setForm] = useState({ nome: "", email: "", senha: "", nomeEmpresa: "" });
  const [erro, setErro] = useState(null);
  // So no CADASTRO: no login, apontar erro de digitacao no e-mail entregaria quais enderecos existem.
  const campoEmail = useCampoEmail(form.email, (v) => setForm((f) => ({ ...f, email: v })));
  const [carregando, setCarregando] = useState(false);
  const [cadastrouEmail, setCadastrouEmail] = useState(null); // != null → mostra popup pós-cadastro (confirmar e-mail por código)
  const [codigo, setCodigo] = useState("");
  const [codErro, setCodErro] = useState(null);
  const [codLoading, setCodLoading] = useState(false);
  const [reenviado, setReenviado] = useState(false);
  const [verificado, setVerificado] = useState(false);
  const [relatoPublicado, setRelatoPublicado] = useState(null); // id do relato publicado sozinho no cadastro

  const set = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const enviar = async (e) => {
    e.preventDefault();
    setErro(null);
    setCarregando(true);
    try {
      if (modo === "login") {
        await login(form.email, form.senha);
        navigate(destino);
      } else {
        await registrar({
          nome: form.nome, email: form.email, senha: form.senha,
          tipo: Number(tipo), nomeEmpresa: tipo === 1 ? form.nomeEmpresa : null, whatsapp: null,
        });
        // 📝 PUBLICA O RELATO NA HORA EM QUE A CONTA NASCE. A pessoa já clicou em "Publicar" — só faltava a conta.
        // Em 10/09 a Lucimar escreveu 3 min, criou a conta, saiu na tela do código (foi buscar no Hotmail) e o
        // relato ficou só no navegador dela. Publicando aqui, não sobra passo para abandonar.
        // Exceção: informou e-mail do recrutador sem marcar ciência (LGPD) — aí volta ao formulário, que pergunta.
        if (destino === "/denunciar") {
          try {
            const r = JSON.parse(localStorage.getItem(RASCUNHO_KEY) || "null");
            const faltaCiencia = r && (r.contatoEmail || "").trim() && !r.cienteContato;
            // Tinha anexo? Não publica sozinho: o arquivo se perdeu no cadastro e não há onde anexar depois.
            if (r && (r.titulo || "").trim() && (r.descricao || "").trim() && !faltaCiencia && !r.teveAnexos) {
              const nova = await criarDenuncia(payloadDoRelato(r, { nome: form.nome }));
              try { localStorage.removeItem(RASCUNHO_KEY); } catch { /* ok */ }
              setRelatoPublicado(nova.id);
            }
          } catch { /* falhou: o rascunho continua no navegador e o bloco laranja leva de volta ao formulário */ }
        }
        setCadastrouEmail(form.email); // conta criada (auto-login já feito) → popup de boas-vindas; navega no "Continuar"
        // 📊 Fecha o funil da vaga: a pessoa veio de uma vaga (?redirect=) e CONCLUIU a conta. É o passo
        // que diz se a exigência de cadastro derruba mais gente em vaga de handoff — ver PassoFunilVaga.
        const daVaga = (destino || "").match(/^\/vagas\/(\d+)/);
        if (daVaga) passoFunilVaga(Number(daVaga[1]), "cadastrou");
      }
    } catch (e) {
      setErro(e.message || "Falha na autenticação.");
    } finally {
      setCarregando(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary outline-none";

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-md mx-auto px-4 py-10">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          {motivo && (
            <div className="mb-5 rounded-xl border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
              📣 {motivo}
            </div>
          )}
          <div className="flex gap-2 mb-6">
            <Tab ativo={modo === "login"} onClick={() => setModo("login")}>Entrar</Tab>
            <Tab ativo={modo === "cadastro"} onClick={() => setModo("cadastro")}>Cadastrar</Tab>
          </div>

          {erro && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 text-sm">{erro}</div>}

          <form onSubmit={enviar} className="space-y-4">
            {modo === "cadastro" && (
              <>
                <div className="flex gap-2">
                  <TipoBtn ativo={tipo === 0} onClick={() => setTipo(0)}>Sou candidato</TipoBtn>
                  <TipoBtn ativo={tipo === 1} onClick={() => setTipo(1)}>Empresa / recrutador</TipoBtn>
                </div>
                <input className={inputCls} autoComplete="name" aria-label={tipo === 1 ? "Seu nome (responsável)" : "Seu nome"} placeholder={tipo === 1 ? "Seu nome (responsável)" : "Seu nome"} value={form.nome} onChange={set("nome")} required />
                {tipo === 1 && (
                  <input className={inputCls} aria-label="Nome da empresa" placeholder="Nome da empresa" value={form.nomeEmpresa} onChange={set("nomeEmpresa")} required />
                )}
              </>
            )}
            <input type="email" className={inputCls} autoComplete="email" aria-label="E-mail" placeholder={modo === "cadastro" && tipo === 1 ? "E-mail corporativo (ex: nome@suaempresa.com.br)" : "E-mail"} value={form.email}
              onChange={(e) => { set("email")(e); campoEmail.aoDigitar(); }} onBlur={campoEmail.aoSair} required />
            {modo === "cadastro" && <AvisoEmail {...campoEmail.aviso} />}
            {modo === "cadastro" && tipo === 1 && (
              <p className="text-[11px] text-primary -mt-1.5 leading-relaxed">📧 Use o e-mail <strong>corporativo</strong> da empresa. Gmail, Hotmail e Outlook não são aceitos para conta de empresa, e o cadastro passa por aprovação.</p>
            )}
            <input type="password" className={inputCls} aria-label="Senha" placeholder="Senha" value={form.senha} onChange={set("senha")}
              autoComplete={modo === "login" ? "current-password" : "new-password"} minLength={modo === "cadastro" ? 6 : undefined} required />

            <button type="submit" disabled={carregando}
              className="w-full bg-primary hover:opacity-90 disabled:opacity-60 text-white font-bold py-2.5 rounded-lg">
              {carregando ? "Aguarde..." : modo === "login" ? "Entrar" : "Criar conta"}
            </button>
            {modo === "cadastro" && (
              <>
                <p className="text-[11px] text-gray-500 mt-3 text-center leading-relaxed flex items-center justify-center gap-1">
                  🔒 Conexão segura (HTTPS). Sua senha é protegida e nunca fica visível — nem para a nossa equipe.
                </p>
                <p className="text-[11px] text-gray-400 mt-2 text-center leading-relaxed">
                  Seus dados são tratados conforme a <Link to="/privacidade" className="text-primary hover:underline">LGPD</Link>. Ao criar conta, você concorda com os <Link to="/termos" className="text-primary hover:underline">Termos de Uso</Link> e a <Link to="/privacidade" className="text-primary hover:underline">Política de Privacidade</Link>.
                </p>
              </>
            )}
          </form>

          {modo === "login" && (
            <p className="text-sm mt-4 text-center">
              <button type="button" onClick={() => navigate("/esqueci-senha")} className="text-primary font-semibold hover:underline">
                Esqueci minha senha
              </button>
            </p>
          )}
        </div>
      </main>

      {/* Popup pós-cadastro: confirma o e-mail por CÓDIGO de 6 dígitos (com aviso de spam) — mesmo fluxo do cadastro de empresa. */}
      {cadastrouEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
            {verificado ? (
              <>
                <div className="text-4xl mb-1">✅</div>
                <h2 className="text-lg font-bold text-text">E-mail confirmado!</h2>
                <p className="text-sm text-gray-600 mt-2">Sua conta está <strong>✓ verificada</strong>. Bem-vindo(a) ao eaiRH!</p>
                <button onClick={() => navigate(destino)} className="mt-4 w-full rounded-xl bg-primary text-white font-semibold py-2.5 hover:opacity-90">
                  {destino === "/denunciar" ? "Voltar e publicar meu relato" : "Continuar"}
                </button>
              </>
            ) : (
              <>
                {/* 🚨 QUEM VEIO DE UM RELATO PRECISA SABER QUE ELE AINDA NÃO SAIU. Em 10/09 a Lucimar escreveu 3 min em
                    /denunciar, criou a conta, caiu aqui — que só falava em "selo verificado", com o caminho de volta num
                    "Confirmar depois" cinza no canto — e foi embora. O relato ficou só no navegador dela. */}
                {relatoPublicado && (
                  <div className="mb-4 rounded-xl border border-green-300 bg-green-50 p-3 text-left">
                    <p className="text-sm font-bold text-green-900">✅ Seu relato foi publicado!</p>
                    <Link to={`/relato/${relatoPublicado}`} className="text-sm text-green-800 font-semibold hover:underline">Ver meu relato →</Link>
                    <p className="text-[11px] text-green-800 mt-1">Vamos te avisar por e-mail se a empresa responder.</p>
                  </div>
                )}
                {destino === "/denunciar" && !relatoPublicado && (
                  <div className="mb-4 rounded-xl border border-orange-300 bg-orange-50 p-3 text-left">
                    <p className="text-sm font-bold text-orange-900">📝 Seu relato está guardado, mas ainda não foi publicado</p>
                    <button type="button" onClick={() => navigate(destino)}
                      className="mt-2 w-full rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2.5">
                      Voltar e publicar meu relato
                    </button>
                    <p className="text-[11px] text-orange-800 mt-2">Confirmar o e-mail é opcional agora — dá para fazer depois.</p>
                  </div>
                )}
                <div className="text-4xl mb-1">✉️</div>
                <h2 className="text-lg font-bold text-text">Confirme seu e-mail</h2>
                <p className="text-sm text-gray-600 mt-2">
                  Enviamos um <strong>código de 6 dígitos</strong> para <strong className="break-all">{cadastrouEmail}</strong>. Digite-o para ganhar o selo <strong>✓ verificado</strong>.
                </p>
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-3 text-left">
                  📬 <strong>Não chegou em 1–2 minutos?</strong> Confira o <strong>spam / lixo eletrônico</strong> (e "Promoções", no Gmail) e marque como <strong>“não é spam”</strong>.
                </p>
                {codErro && <p role="alert" className="text-red-600 text-sm mt-3">{codErro}</p>}
                <form onSubmit={async (e) => {
                  e.preventDefault(); setCodErro(null); setCodLoading(true);
                  try { await confirmarCodigo(cadastrouEmail, codigo); setVerificado(true); }
                  catch (err) { setCodErro(err.message || "Código inválido."); }
                  finally { setCodLoading(false); }
                }} className="mt-3 space-y-3">
                  <input className="w-full rounded-lg border border-gray-300 px-3 py-3 text-center text-2xl font-bold tracking-[0.5em] focus:ring-2 focus:ring-primary outline-none"
                    inputMode="numeric" autoComplete="one-time-code" maxLength={6} aria-label="Código de 6 dígitos" placeholder="••••••"
                    value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))} autoFocus required />
                  <button type="submit" disabled={codLoading || codigo.length !== 6}
                    className="w-full rounded-xl bg-primary disabled:opacity-60 text-white font-semibold py-2.5 hover:opacity-90">
                    {codLoading ? "Confirmando..." : "Confirmar e-mail"}
                  </button>
                </form>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <button type="button" onClick={async () => { setCodErro(null); try { await reenviarCodigo(cadastrouEmail); } catch {} setReenviado(true); }}
                    className="text-primary font-semibold hover:underline">{reenviado ? "Código reenviado ✓" : "Reenviar código"}</button>
                  <button type="button" onClick={() => navigate(destino)} className="text-gray-500 hover:underline">Confirmar depois</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Tab({ ativo, children, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={ativo}
      className={`flex-1 py-2 rounded-lg font-semibold text-sm ${ativo ? "bg-primary text-white" : "bg-gray-100 text-gray-600"}`}>
      {children}
    </button>
  );
}
function TipoBtn({ ativo, children, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={ativo}
      className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${ativo ? "border-primary bg-blue-50 text-primary" : "border-gray-200 text-gray-500"}`}>
      {children}
    </button>
  );
}
